// Decides which `sendMessage` socket events are allowed to move the call UI.
//
// WHY THIS EXISTS
// `event_type: 'HANGUP'` used to map to `callStatus: 'WRAPUP'` unconditionally. The
// only thing between a stray teardown and a false wrapup was a guard that could drop a
// HANGUP *only* when it could positively prove the uuid belonged to an earlier call —
// different from the live callId AND already seen. Three kinds of HANGUP therefore got
// through while a call was live: one whose uuid we had never seen (B-leg, bridge,
// agent-leg, switch-rewritten), one carrying no uuid at all, and — separately — any
// unmapped event_type, which produced a null status and wiped ON CALL mid-call.
//
// The cost was not cosmetic. A false WRAPUP starts the wrapup clock, and at the default
// 10s auto-wrapup the CRM is submitted with an AUTOWRAP disposition for a call the agent
// is still on, then the agent is bounced to /dashboard.
//
// Everything here is pure and free of React, Redux, sockets and sip.js so the decision
// table can be tested directly. The stateful half (refs, timers, telemetry) lives in
// CallingSidebar.
//
// THE INVARIANT THAT SHAPES EVERY RULE BELOW
// Never drop a real teardown without a bounded way back. An agent stuck ON CALL forever
// is a worse incident than the spurious WRAPUP this fixes. Every rule that withholds a
// HANGUP is therefore a HOLD with a release condition, never a silent discard.

export const GUARD_STATE = {
  IDLE: 'IDLE',
  DIALING: 'DIALING',
  RINGING: 'RINGING',
  IN_CALL: 'IN_CALL',
  WRAPUP: 'WRAPUP'
};

export const DECISION = {
  // Apply the event and let it set callStatus.
  ACCEPT: 'accept',
  // Run the event's side effects but do NOT let it change callStatus. Used for event
  // types we have no status mapping for — dropping them outright would silently
  // disable onSetCallType and the CRM-fetch branch, which is the tempting wrong fix.
  PASS: 'pass',
  // Not for this call. Ignore entirely.
  DROP: 'drop',
  // A teardown we cannot attribute, arriving while the media is still up. Withheld
  // until the call really ends or the hold expires.
  HOLD: 'hold'
};

// How long an unattributable teardown is withheld when we have no media signal to
// judge it by. Deliberately under the 10s default auto-wrapup so the decision always
// resolves before any auto-submit could fire, and comfortably over normal socket jitter.
export const HANGUP_HOLD_MS = 6000;

// Absolute cap on a hold. Only reachable if the media session claims `live` forever —
// i.e. session tracking itself has gone wrong. This is the bounded escape hatch: past
// this point we would rather wrap up a call wrongly than strand the agent on it.
export const HANGUP_HOLD_MAX_MS = 120000;

// How long a call may sit ON CALL with its media already gone before we conclude the
// teardown was lost and wrap it up ourselves. Long enough that no normal
// hangup-then-teardown sequence reaches it; short enough that an agent is never parked
// on a dead call for a meaningful part of their shift.
export const STUCK_CALL_MS = 30000;

// `call_type` is overloaded — it carries the literal 'HANGUP' as well as the dialling
// mode. `isHangupSignal` folds both spellings together for the guard, while `eventType`
// stays the raw value so the status mapping keeps its existing semantics exactly.
export const normalizeCallEvent = (message) => {
  const eventType = message?.event_type || null;
  const callType = message?.call_type || null;
  return {
    eventType,
    callType,
    uuid: message?.origination_uuid || null,
    isHangupSignal: eventType === 'HANGUP' || callType === 'HANGUP'
  };
};

// Mirrors the existing mapping in CallingSidebar exactly, including predictive's
// EXTENSION_RINGING -> ON CALL shortcut. Any event type without a mapping returns null,
// which is what PASS is for.
export const statusForEvent = (eventType, callType) => {
  if (eventType === 'EXTENSION_RINGING') return callType === 'predictive' ? 'ON CALL' : 'RINGING';
  if (eventType === 'DIALING') return 'DIALING';
  if (eventType === 'ANSWER') return 'ON CALL';
  if (eventType === 'HANGUP') return 'WRAPUP';
  return null;
};

export const guardStateForStatus = (status) => {
  if (status === 'ON CALL') return GUARD_STATE.IN_CALL;
  if (status === 'RINGING') return GUARD_STATE.RINGING;
  if (status === 'DIALING') return GUARD_STATE.DIALING;
  if (status === 'WRAPUP') return GUARD_STATE.WRAPUP;
  return GUARD_STATE.IDLE;
};

const isKnownCall = (uuid, currentUuids) => !!uuid && !!currentUuids && currentUuids.has(uuid);
const wasSeenBefore = (uuid, seenUuids) =>
  !!uuid && Array.isArray(seenUuids) && seenUuids.includes(uuid);

// A teardown is only trustworthy if we can tie it to the call in progress.
const decideHangup = ({ event, guardState, currentUuids, seenUuids, mediaLiveness }) => {
  if (isKnownCall(event.uuid, currentUuids)) {
    return { decision: DECISION.ACCEPT, reason: 'hangup-matches-current-call' };
  }
  // Positively identified as a previous call's teardown — the original guard's rule,
  // preserved.
  if (wasSeenBefore(event.uuid, seenUuids)) {
    return { decision: DECISION.DROP, reason: 'hangup-stale-uuid' };
  }
  // Unknown or missing uuid. With nothing live to protect, accept it: an unrecognised
  // teardown must still tear down, or manual/progressive calls whose HANGUP uuid does
  // not match the originate response would be stranded forever.
  if (guardState !== GUARD_STATE.IN_CALL) {
    return { decision: DECISION.ACCEPT, reason: 'hangup-unattributed-no-live-call' };
  }
  // A call IS live. Ask the media, which cannot be lied to by a late socket event.
  if (mediaLiveness === 'live') {
    return { decision: DECISION.HOLD, reason: 'hangup-unattributed-media-live' };
  }
  if (mediaLiveness === 'ended') {
    return { decision: DECISION.ACCEPT, reason: 'hangup-unattributed-media-ended' };
  }
  // No media signal at all (desk phone). Hold briefly rather than act on an
  // unattributable teardown; the hold expiry applies it, so nothing is stranded.
  return { decision: DECISION.HOLD, reason: 'hangup-unattributed-media-unknown' };
};

// The single decision point. Returns what to do and, on ACCEPT, the guard state to
// advance to.
//
// forceAccept is set when a previously held teardown is being released — the hold has
// already made the decision, so the policy must not run again and re-hold it.
export const decideEvent = ({
  event,
  guardState = GUARD_STATE.IDLE,
  currentUuids,
  seenUuids,
  mediaLiveness = 'unknown',
  forceAccept = false
}) => {
  const status = statusForEvent(event.eventType, event.callType);

  if (event.isHangupSignal) {
    const outcome = forceAccept
      ? { decision: DECISION.ACCEPT, reason: 'hangup-hold-released' }
      : decideHangup({ event, guardState, currentUuids, seenUuids, mediaLiveness });
    return {
      ...outcome,
      status: outcome.decision === DECISION.ACCEPT ? 'WRAPUP' : null,
      nextState: outcome.decision === DECISION.ACCEPT ? GUARD_STATE.WRAPUP : guardState
    };
  }

  // No status mapping for this event type. Let its side effects run, but never let it
  // null out a live status.
  if (!status) {
    return {
      decision: DECISION.PASS,
      reason: 'unmapped-event-type',
      status: null,
      nextState: guardState
    };
  }

  // Mid-call, drop an event we can POSITIVELY identify as a late repaint from a call
  // that is already over — its uuid is one we have seen live and it is not this call.
  //
  // Two deliberate limits, both there to avoid stranding the agent:
  //   - an UNKNOWN uuid is not dropped. A teardown for the previous call would carry a
  //     uuid we have seen; a uuid we have never seen is far more likely a new call
  //     whose earlier events we missed, and dropping it would leave the agent stuck on
  //     a call that has already moved on.
  //   - scoped to IN_CALL only. From WRAPUP a new call's ANSWER must get through: the
  //     upstream EXTENSION_RINGING guard already drops the ring, which makes ANSWER the
  //     only event that can start the next predictive call.
  if (
    guardState === GUARD_STATE.IN_CALL &&
    event.uuid &&
    !isKnownCall(event.uuid, currentUuids) &&
    wasSeenBefore(event.uuid, seenUuids)
  ) {
    return {
      decision: DECISION.DROP,
      reason: 'stale-event-for-previous-call',
      status: null,
      nextState: guardState
    };
  }

  return {
    decision: DECISION.ACCEPT,
    reason: 'ok',
    status,
    nextState: guardStateForStatus(status)
  };
};
